import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Avatar,
  Typography,
  Chip,
  Grid,
  Rating,
} from '@mui/material';
import { LocationOn, Work, Language } from '@mui/icons-material';
import { CaretakerProfile } from '../services/caretakerProfileService';

interface CaretakerProfileHeaderProps {
  caretaker: CaretakerProfile;
}

const CaretakerProfileHeader: React.FC<CaretakerProfileHeaderProps> = ({ caretaker }) => {
  const getAvatarUrl = (): string => {
    if (caretaker.avatar) {
      return caretaker.avatar;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(caretaker.displayName)}&background=random&color=fff&size=300`;
  };

  const getPrimarySpecialty = (): string => {
    return caretaker.specialties.length > 0 
      ? caretaker.specialties[0].name 
      : 'Healthcare Provider';
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Grid container spacing={3} alignItems="center">
          {/* Avatar */}
          <Grid item xs={12} sm={3} md={2}>
            <Box display="flex" justifyContent="center">
              <Avatar
                src={getAvatarUrl()}
                alt={caretaker.displayName}
                sx={{
                  width: { xs: 120, sm: 150 },
                  height: { xs: 120, sm: 150 },
                  border: '4px solid',
                  borderColor: 'primary.main',
                }}
              />
            </Box>
          </Grid>

          {/* Profile Info */}
          <Grid item xs={12} sm={9} md={10}>
            <Box>
              {/* Name and Specialty */}
              <Typography variant="h3" component="h1" gutterBottom>
                {caretaker.displayName}
              </Typography>
              
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Chip
                  label={getPrimarySpecialty()}
                  color="primary"
                  size="medium"
                  icon={<Work />}
                />
                {caretaker.specialties.length > 1 && (
                  <Chip
                    label={`+${caretaker.specialties.length - 1} more specialties`}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              {/* Rating */}
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Rating
                  value={caretaker.averageRating}
                  precision={0.1}
                  readOnly
                  size="large"
                />
                <Typography variant="h6" color="text.secondary">
                  {caretaker.averageRating} ({caretaker.reviewCount} review{caretaker.reviewCount !== 1 ? 's' : ''})
                </Typography>
              </Box>

              {/* Location and Languages */}
              <Grid container spacing={2}>
                {caretaker.location && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn color="action" />
                      <Typography variant="body1">
                        {caretaker.location}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Language color="action" />
                    <Typography variant="body1">
                      {caretaker.languagesText}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Experience and Rate */}
              <Box display="flex" alignItems="center" gap={4} mt={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Experience
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {caretaker.experienceText}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Hourly Rate
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {caretaker.hourlyRateText}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CaretakerProfileHeader;
